import pick from 'lodash-es/pick'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { FILTER_NAME_SUB_MENU } from '../../constants/filter'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../constants/validation'
import { isFolderFile } from '../../utils/file'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../utils/massActions'
import { isStaffUser } from '../../utils/user'
import { parseServerValidation } from '../../utils/validation'

import {
  DOCUMENT_ACCESS_LEVEL,
  DOCUMENT_ALL_PERMISSION_GROUPS,
  DOCUMENT_ALL_PERMISSION_GROUPS_ALLOW,
  DOCUMENTS_ACTION_DOWNLOAD,
  DOCUMENTS_ACTION_DOWNLOAD_MASS,
  DOCUMENTS_ACTION_DUPLICATE,
  DOCUMENTS_ACTION_EDIT,
  DOCUMENTS_ACTION_EDIT_ACCESS,
  DOCUMENTS_ACTION_EDIT_ACCESS_MASS,
  DOCUMENTS_ACTION_MOVE_TO,
  DOCUMENTS_ACTION_MOVE_TO_MASS,
  DOCUMENTS_ACTION_REMOVE,
  DOCUMENTS_ACTION_REMOVE_MASS,
  DOCUMENTS_ACTION_SEND_VIA_EMAIL,
  DOCUMENTS_ACTION_SEND_VIA_EMAIL_MASS,
  DOCUMENTS_ACTION_VIEW_REPORT,
  DOCUMENTS_FILTER_KEY_PARENT,
  DROP_TARGET_DATA_KEY,
} from './Document.constants'

export function getIdwellToIdwellUploadProps(outboundUuid) {
  return outboundUuid
    ? {
        permissionGroups: DOCUMENT_ALL_PERMISSION_GROUPS_ALLOW,
        skipAccessModal: true,
      }
    : EMPTY_OBJECT
}

// Skips the access modal; groups outside the set are denied explicitly because
// the backend defaults new files to all groups.
export function getDocumentsFixedAccessUploadProps(permissionGroups) {
  return {
    permissionGroups: Object.fromEntries(
      DOCUMENT_ALL_PERMISSION_GROUPS.map(role => [
        role,
        permissionGroups.includes(role)
          ? DOCUMENT_ACCESS_LEVEL.allow
          : DOCUMENT_ACCESS_LEVEL.deny,
      ])
    ),
    skipAccessModal: true,
  }
}

export function getAsyncNotificationText({ action, t }) {
  const types = {
    [DOCUMENTS_ACTION_EDIT_ACCESS_MASS]: t(
      'Notification.DocumentsAsyncTextMassEditAccessKey'
    ),
    [DOCUMENTS_ACTION_REMOVE]: t(
      'Notification.DocumentsAsyncTextMassDeleteKey'
    ),
    [DOCUMENTS_ACTION_REMOVE_MASS]: t(
      'Notification.DocumentsAsyncTextMassDeleteKey'
    ),
  }

  return types[action] || null
}

export function getNotificationTitle({ action, count, t, totalCount }) {
  const types = {
    [DOCUMENTS_ACTION_DUPLICATE]: t('Notification.CommonTitleDuplicateKey'),
    [DOCUMENTS_ACTION_EDIT]: t('Notification.CommonTitleEditeKey'),
    [DOCUMENTS_ACTION_EDIT_ACCESS_MASS]: t(
      'Notification.CommonTitleEditAccessKey'
    ),
    [DOCUMENTS_ACTION_MOVE_TO]: t('Notification.CommonMovedEditeKey'),
    [DOCUMENTS_ACTION_MOVE_TO_MASS]: t('Notification.CommonTitleMassMovedKey', {
      count,
      totalCount,
    }),
    [DOCUMENTS_ACTION_REMOVE]: t('Notification.CommonTitleDeletedKey'),
    [DOCUMENTS_ACTION_REMOVE_MASS]: t(
      'Notification.CommonTitleMassDeletedKey',
      {
        count,
        totalCount,
      }
    ),
  }

  return types[action] || null
}

export function isVulcavoFileTooLargeError(error) {
  const serverError = parseServerValidation(error?.response?.data)
  const message = serverError?.[SERVER_NON_FIELD_ERROR_KEY] ?? EMPTY_STRING

  return /\b100\s*mb\b/i.test(message)
}

export function getDocumentsPermanentFilters({ filterValues }) {
  return pick(filterValues, [DOCUMENTS_FILTER_KEY_PARENT, FILTER_NAME_SUB_MENU])
}

export function parseDocumentsPermanentFilters(filters) {
  return [
    [DOCUMENTS_FILTER_KEY_PARENT, filters.parent],
    [FILTER_NAME_SUB_MENU, filters.sub_menu],
  ]
}

export function getOnClickProps(item) {
  return item
}

export function getDraggableRowProps(item) {
  if (isFolderFile(item) && item.permissions?.can_write) {
    return { [DROP_TARGET_DATA_KEY]: item.id }
  }

  return EMPTY_OBJECT
}

export function isDraggingSelectedItem({
  activeId,
  excludeIds,
  selectAllPages,
  selectedIds,
}) {
  // With "select all pages" every item is selected except the excluded ones,
  // so an excluded item counts as dragging a single (unselected) file.
  if (selectAllPages) {
    return !excludeIds?.some(id => String(id) === String(activeId))
  }

  return selectedIds?.some(id => String(id) === String(activeId))
}

export function getDraggedItemsCount({
  activeId,
  excludeIds,
  pagination,
  selectAllPages,
  selectedIds,
}) {
  if (
    !isDraggingSelectedItem({
      activeId,
      excludeIds,
      selectAllPages,
      selectedIds,
    })
  ) {
    return 1
  }

  const count = selectAllPages
    ? (pagination?.count ?? 1) - (excludeIds?.length ?? 0)
    : selectedIds?.length

  return count > 0 ? count : 1
}

// `extraActions` is the slot between Edit and Delete that varies per entity:
// the generic set (Duplicate + View report) by default; the Request tab injects
// Mark as invoice + Duplicate to related there instead.
export function getDocumentsItemActions({
  canDelete = true,
  canEditAccess = true,
  canSendViaEmail = true,
  commonPermissions,
  extraActions,
  item,
  user,
}) {
  const { permissions = EMPTY_OBJECT } = item

  const isStaff = isStaffUser(user)
  const isFolder = isFolderFile(item)

  const middleActions = extraActions ?? [
    {
      label: 'Documents.ViewReportActionOptionKey',
      permission: isStaff && !isFolder,
      value: DOCUMENTS_ACTION_VIEW_REPORT,
    },
  ]

  const items = [
    {
      label: 'Documents.SendViaEmailActionOptionKey',
      permission: canSendViaEmail && isStaff && !isFolder,
      value: DOCUMENTS_ACTION_SEND_VIA_EMAIL,
    },
    {
      label: 'Documents.DownloadActionOptionKey',
      permission: permissions.can_download,
      value: DOCUMENTS_ACTION_DOWNLOAD,
    },
    {
      label: 'Documents.MoveToActionOptionKey',
      permission: permissions.can_move,
      value: DOCUMENTS_ACTION_MOVE_TO,
    },
    {
      label: 'Documents.EditAccessActionOptionKey',
      permission:
        canEditAccess &&
        permissions.can_edit &&
        commonPermissions?.can_edit_user_access,
      value: DOCUMENTS_ACTION_EDIT_ACCESS,
    },
    {
      label: 'Documents.EditActionOptionKey',
      permission: permissions.can_edit,
      value: DOCUMENTS_ACTION_EDIT,
    },
    {
      label: 'Documents.DuplicateActionOptionKey',
      permission: !isFolder,
      value: DOCUMENTS_ACTION_DUPLICATE,
    },
    ...middleActions,
    null,
    {
      label: 'Documents.DeleteActionOptionKey',
      permission: canDelete && permissions.can_delete,
      value: DOCUMENTS_ACTION_REMOVE,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getDocumentsMassActions({
  canEditAccess = true,
  contextData,
  permissions,
  selectAllPages,
  selectedFiles,
  t,
  user,
}) {
  // Sending via email isn't available for connected (iDWELL-to-iDWELL) requests.
  const canSendViaEmail = !contextData?.outboundUuid

  // Deleting files/directories is blocked (backend returns 403) when the
  // request doesn't allow it (e.g. status is not OPENED, or a connected
  // request). Non-request document contexts don't send this flag.
  const canDelete =
    contextData?.requestData?.permissions?.can_edit_rm_files !== false

  const items = [
    {
      label: t('Documents.SendViaEmailMassActionOptionKey'),
      permission:
        canSendViaEmail &&
        isStaffUser(user) &&
        !selectAllPages &&
        selectedFiles.length > 0,
      value: DOCUMENTS_ACTION_SEND_VIA_EMAIL_MASS,
    },
    {
      label: t('Documents.DownloadMassActionOptionKey'),
      permission: permissions.can_download,
      value: DOCUMENTS_ACTION_DOWNLOAD_MASS,
    },
    {
      label: t('Documents.MoveToMassActionOptionKey'),
      permission: permissions.can_move,
      value: DOCUMENTS_ACTION_MOVE_TO_MASS,
    },
    {
      label: t('Documents.EditAccessMassActionOptionKey'),
      permission: canEditAccess && permissions.can_edit_user_access,
      value: DOCUMENTS_ACTION_EDIT_ACCESS_MASS,
    },
    null,
    {
      label: t('Documents.DeleteMassActionOptionKey'),
      permission: canDelete && permissions.can_delete,
      value: DOCUMENTS_ACTION_REMOVE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}
